import { useCallback, useId, useState } from 'react';
import type * as React from 'react';
import { useCreate, useDelete, useUpdate } from '@refinedev/core';
import { Plus, Trash2 } from 'lucide-react';
import { Page, Notice } from '@/components/page';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/states';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogForm,
  DialogBody,
  DialogContent,
  DialogFooter,
} from '@/components/ui/dialog';
import { FormField, Input } from '@/components/ui/field';
import { IconField } from '@/components/icon-field';
import { Badge, Switch, Tooltip } from '@/components/ui/primitives';
import { RESOURCES, type AdminChildEventType } from '@/api/resources';
import { isInUse, useAdminList } from '@/hooks/use-admin-resource';
import { ApiError } from '@/api/errors';
import { notify } from '@/lib/notify';
import { formatNumber } from '@/lib/format';

/**
 * Child event types — the Add Event picker (06 §2, WP-15).
 *
 * ⚠️ A type an event already uses is never deleted. The API answers
 * `409 OPTION_IN_USE`, and `usageCount` is on the read DTO precisely so the
 * panel can offer "deactivate" *before* the admin clicks rather than explaining
 * a refusal afterwards — deactivating removes it from the picker while every
 * event already created with it keeps it.
 *
 * ⚠️ `code` is shown but never editable after creation. The API branches on it
 * — an event whose type is `birthday` suppresses that child's derived birthday
 * in the upcoming-events list — so renaming one would silently change behaviour
 * rather than fail. The label is free to be reworded.
 */
export function EventTypesPage(): React.JSX.Element {
  const { rows, isLoading, error, refetch, invalidate } = useAdminList<AdminChildEventType>(
    RESOURCES.childEventTypes,
  );

  const [editing, setEditing] = useState<AdminChildEventType | 'new' | null>(null);
  const [deleting, setDeleting] = useState<AdminChildEventType | null>(null);

  const { mutateAsync: update } = useUpdate();
  const { mutateAsync: remove } = useDelete();

  const toggleActive = useCallback(
    async (type: AdminChildEventType) => {
      try {
        await update({
          resource: RESOURCES.childEventTypes,
          id: type.id,
          values: { isActive: !type.isActive },
          successNotification: false,
          errorNotification: false,
        });
        notify.success(type.isActive ? 'Deactivated' : 'Activated', type.label);
      } catch (err) {
        notify.error(err, 'The change was not saved');
      } finally {
        invalidate();
      }
    },
    [update, invalidate],
  );

  return (
    <Page
      title="Event types"
      description="What a parent can pick when adding an event to a child's profile."
      breadcrumbs={[{ label: 'Master data' }, { label: 'Event types' }]}
      action={
        <Button variant="primary" onClick={() => setEditing('new')}>
          <Plus aria-hidden="true" />
          Add event type
        </Button>
      }
    >
      <div className="space-y-5">
        <Notice title="In use means deactivate, not delete">
          A type parents have already used cannot be deleted — deactivating removes it from the
          picker while every event created with it keeps it. The code cannot be changed once
          created: the API relies on it (a `birthday` type replaces the derived birthday).
        </Notice>

        <div className="overflow-hidden rounded-card border border-line bg-surface">
          {error ? (
            <ErrorState error={error} onRetry={refetch} endpoint="GET /admin/child-event-types" />
          ) : isLoading ? (
            <TableSkeleton columns={5} />
          ) : !rows || rows.length === 0 ? (
            <EmptyState
              title="No event types yet"
              description="Add the first type and parents can pick it straight away."
              action={
                <Button variant="primary" onClick={() => setEditing('new')}>
                  <Plus aria-hidden="true" />
                  Add event type
                </Button>
              }
            />
          ) : (
            <ul className="divide-y divide-line">
              {rows.map((type) => (
                <li key={type.id} className="px-4 py-3">
                  <div className="flex flex-wrap items-center gap-3">
                    {type.iconUrl ? (
                      <img
                        src={type.iconUrl}
                        alt=""
                        width={28}
                        height={28}
                        className="size-7 shrink-0 rounded object-cover"
                      />
                    ) : (
                      <span className="size-7 shrink-0 rounded border border-line bg-surface-sunken" />
                    )}

                    <button
                      type="button"
                      className="min-w-0 flex-1 rounded text-left"
                      onClick={() => setEditing(type)}
                    >
                      <span className="block truncate text-sm font-medium text-ink">
                        {type.label}
                      </span>
                      <span className="block truncate font-mono text-xs text-ink-soft">
                        {type.code}
                      </span>
                    </button>

                    <span className="text-xs text-ink-soft" data-numeric>
                      {formatNumber(type.usageCount)} events
                    </span>

                    {type.isActive ? (
                      <Badge tone="success">Active</Badge>
                    ) : (
                      <Badge tone="neutral">Inactive</Badge>
                    )}

                    <Tooltip label={type.isActive ? 'Deactivate' : 'Activate'}>
                      <span>
                        <Switch
                          checked={type.isActive}
                          onCheckedChange={() => void toggleActive(type)}
                          aria-label={`${type.isActive ? 'Deactivate' : 'Activate'} ${type.label}`}
                        />
                      </span>
                    </Tooltip>

                    <Tooltip
                      label={
                        type.usageCount > 0
                          ? 'In use by existing events — deactivate instead'
                          : 'Delete'
                      }
                    >
                      <span>
                        <Button
                          size="icon"
                          variant="ghost"
                          disabled={type.usageCount > 0}
                          aria-label={`Delete ${type.label}`}
                          onClick={() => setDeleting(type)}
                        >
                          <Trash2 aria-hidden="true" />
                        </Button>
                      </span>
                    </Tooltip>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {editing ? (
        <EventTypeDialog
          type={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            invalidate();
          }}
        />
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        title={`Delete ${deleting?.label ?? ''}?`}
        description="This removes the event type entirely. It cannot be undone."
        confirmLabel="Delete"
        destructive
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await remove({
              resource: RESOURCES.childEventTypes,
              id: deleting.id,
              successNotification: false,
              errorNotification: false,
            });
            notify.success('Deleted', deleting.label);
            setDeleting(null);
          } catch (err) {
            if (isInUse(err)) {
              notify.error(err, 'Still in use');
            } else {
              notify.error(err, 'The event type was not deleted');
            }
          } finally {
            invalidate();
          }
        }}
      />
    </Page>
  );
}

// ---------------------------------------------------------------------------

/** Suggests a code from the label, so an admin never has to invent one. */
function slugify(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

function EventTypeDialog({
  type,
  onClose,
  onSaved,
}: {
  type: AdminChildEventType | null;
  onClose: () => void;
  onSaved: () => void;
}): React.JSX.Element {
  const [label, setLabel] = useState(type?.label ?? '');
  const [code, setCode] = useState(type?.code ?? '');
  // Tracks whether the admin has typed their own code. Until they do, the code
  // follows the label — which is what makes the field feel automatic without
  // ever overwriting a deliberate choice.
  const [codeTouched, setCodeTouched] = useState(false);
  const [iconKey, setIconKey] = useState<string | null | undefined>(undefined);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);

  const labelId = useId();
  const codeId = useId();

  const { mutateAsync: create } = useCreate();
  const { mutateAsync: update } = useUpdate();

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setPending(true);
    setFieldErrors({});

    try {
      if (type) {
        // No `code`: it is immutable, and the API does not accept it on PATCH.
        await update({
          resource: RESOURCES.childEventTypes,
          id: type.id,
          values: { label: label.trim(), ...(iconKey !== undefined ? { iconKey } : {}) },
          successNotification: false,
          errorNotification: false,
        });
        notify.success('Saved', label.trim());
      } else {
        await create({
          resource: RESOURCES.childEventTypes,
          values: { code: code.trim(), label: label.trim(), iconKey },
          successNotification: false,
          errorNotification: false,
        });
        notify.success('Added', label.trim());
      }
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) setFieldErrors(err.fieldErrors);
      notify.error(err, 'The event type was not saved');
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        title={type ? 'Edit event type' : 'Add event type'}
        description="Labels appear in the app exactly as typed."
      >
        <DialogForm
          onSubmit={(event) => {
            void submit(event);
          }}
          noValidate
        >
          <DialogBody>
            <FormField label="Label" htmlFor={labelId} error={fieldErrors['label']} required>
              <Input
                value={label}
                onChange={(event) => {
                  setLabel(event.target.value);
                  if (!type && !codeTouched) setCode(slugify(event.target.value));
                }}
                maxLength={60}
                autoFocus
                required
              />
            </FormField>

            <FormField
              label="Code"
              htmlFor={codeId}
              error={fieldErrors['code']}
              required={!type}
              hint={
                type
                  ? 'Fixed once created — the API branches on it.'
                  : 'Lowercase and underscores. Cannot be changed later.'
              }
            >
              <Input
                value={code}
                onChange={(event) => {
                  setCodeTouched(true);
                  setCode(slugify(event.target.value));
                }}
                maxLength={60}
                disabled={type !== null}
                required={!type}
              />
            </FormField>

            <IconField
              onChange={setIconKey}
              initialPreviewUrl={type?.iconUrl ?? null}
              error={fieldErrors['iconKey']}
              required
            />
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={pending}
              disabled={!label.trim() || (!type && (!code.trim() || !iconKey))}
            >
              {type ? 'Save changes' : 'Add event type'}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
